import { useEffect, useState } from "react";
import { MoveHorizontal, Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ACTIVITY_TYPES, formatDay, mediaSrc } from "../../lib/labels";

const latestPhoto = (projectId, year) =>
  api.get(`/projects/${projectId}/photo-history`, { params: { year, latest: true, limit: 5 } }).then((r) => {
    for (const a of r.data) {
      const photos = a.media.filter((m) => m.type === "PHOTO");
      if (photos.length) return { ...photos[photos.length - 1], a };
    }
    return null;
  });

const loadImg = async (m) => {
  const r = await fetch(mediaSrc(m), { credentials: "include" });
  if (!r.ok) throw new Error("img");
  return createImageBitmap(await r.blob());
};

const drawCover = (ctx, img, x, y, w, h) => {
  const s = Math.max(w / img.width, h / img.height);
  const sw = w / s, sh = h / s;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
};

const caption = (m) => `${formatDay(m.a.activityDate)} · ${ACTIVITY_TYPES[m.a.activityType] || ""}`;

const buildImage = async (pair, years, title) => {
  const [b, a] = await Promise.all(pair.map(loadImg));
  const W = 1600, H = 1170, PH = 900, TOP = 110;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#064e3b"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#fff"; ctx.font = "bold 40px sans-serif"; ctx.fillText("Kebun Dulu dan Kini", 40, 58);
  ctx.font = "26px sans-serif"; ctx.fillStyle = "#a7f3d0"; ctx.fillText(title.slice(0, 90), 40, 94);
  [[b, 0, `DULU · ${years[0]}`, pair[0]], [a, W / 2, `KINI · ${years[1]}`, pair[1]]].forEach(([img, x, label, m]) => {
    drawCover(ctx, img, x + (x ? 4 : 0), TOP, W / 2 - 4, PH);
    ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.fillRect(x + 24, TOP + 24, 300, 56);
    ctx.fillStyle = "#fff"; ctx.font = "bold 30px sans-serif"; ctx.fillText(label, x + 40, TOP + 63);
    ctx.fillStyle = "#fff"; ctx.font = "24px sans-serif"; ctx.fillText(caption(m).slice(0, 55), x + 40, TOP + PH + 50);
  });
  ctx.fillStyle = "#fcd34d"; ctx.font = "20px sans-serif";
  ctx.fillText("Salimbado Farm × PT SJI — foto bukti asli dari kelompok tani", 40, H - 22);
  return new Promise((res) => c.toBlob(res, "image/jpeg", 0.88));
};

const ShareButtons = ({ pair, years, title }) => {
  const [busy, setBusy] = useState(false);
  const name = `dulu-kini-${years[0]}-${years[1]}.jpg`;
  const run = async (share) => {
    setBusy(true);
    try {
      const blob = await buildImage(pair, years, title);
      const file = new File([blob], name, { type: "image/jpeg" });
      if (share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Kebun Dulu dan Kini", text: title });
      } else {
        const url = URL.createObjectURL(blob);
        const link = Object.assign(document.createElement("a"), { href: url, download: name });
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        toast.success("Gambar perbandingan disimpan");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Gambar gagal dibuat. Coba lagi.");
    } finally { setBusy(false); }
  };
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const cls = "inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-sm font-semibold disabled:opacity-60";
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" disabled={busy} onClick={() => run(false)} className={`${cls} bg-emerald-800 text-white`} data-testid="compare-download-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Simpan Gambar
      </button>
      {canShare && <button type="button" disabled={busy} onClick={() => run(true)} className={`${cls} border text-emerald-800`} data-testid="compare-share-btn"><Share2 className="h-4 w-4" />Bagikan</button>}
    </div>
  );
};

const YearSelect = ({ value, years, other, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-lg border bg-white px-2 text-sm font-semibold" data-testid={testid}>
    {years.map((y) => <option key={y} value={y} disabled={y === other}>{y}</option>)}
  </select>
);

const Caption = ({ label, m }) => (
  <p className="text-[11px] text-slate-600 truncate"><b className="text-slate-900">{label}</b> · {m ? `${formatDay(m.a.activityDate)} · ${ACTIVITY_TYPES[m.a.activityType] || ""}` : "belum ada foto"}</p>
);

const Slider = ({ before, after, pos, setPos }) => (
  <div className="relative aspect-[4/3] sm:aspect-[16/9] rounded-xl overflow-hidden border bg-slate-100 select-none" data-testid="compare-slider">
    <img src={mediaSrc(after)} alt="Foto tahun ini" className="absolute inset-0 h-full w-full object-cover" data-testid="compare-after-img" />
    <img src={mediaSrc(before)} alt="Foto tahun lalu" className="absolute inset-0 h-full w-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} data-testid="compare-before-img" />
    <span className="absolute top-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">DULU</span>
    <span className="absolute top-2 right-2 rounded bg-emerald-800/90 px-2 py-0.5 text-[10px] font-bold text-white">KINI</span>
    <div className="absolute inset-y-0 w-0.5 bg-white shadow pointer-events-none" style={{ left: `${pos}%` }}>
      <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full bg-white shadow grid place-items-center"><MoveHorizontal className="h-4 w-4 text-emerald-800" /></span>
    </div>
    <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} aria-label="Geser perbandingan foto"
      className="absolute inset-0 h-full w-full opacity-0 cursor-ew-resize" data-testid="compare-range" />
  </div>
);

export const YearCompare = ({ projectId, years, title = "" }) => {
  const [a, setA] = useState(years[years.length - 2]);
  const [b, setB] = useState(years[years.length - 1]);
  const [pair, setPair] = useState(null);
  const [pos, setPos] = useState(50);
  useEffect(() => {
    setPair(null);
    Promise.all([latestPhoto(projectId, a), latestPhoto(projectId, b)]).then(setPair).catch(() => setPair([null, null]));
  }, [projectId, a, b]);
  return (
    <section className="bg-white border rounded-xl p-4 sm:p-5 space-y-3" data-testid="year-compare">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="w-full sm:w-auto text-sm font-bold uppercase tracking-wide text-slate-900 sm:mr-auto">Kebun Dulu dan Kini</h2>
        <YearSelect value={a} years={years} other={b} onChange={setA} testid="compare-year-before" />
        <span className="text-sm text-slate-500">vs</span>
        <YearSelect value={b} years={years} other={a} onChange={setB} testid="compare-year-after" />
      </div>
      {!pair ? <div className="aspect-[16/9] rounded-xl bg-slate-100 animate-pulse" />
        : pair[0] && pair[1] ? <Slider before={pair[0]} after={pair[1]} pos={pos} setPos={setPos} />
        : <p className="text-xs text-slate-500" data-testid="compare-empty">Belum ada foto pada salah satu tahun yang dipilih.</p>}
      {pair && <div className="grid grid-cols-2 gap-2"><Caption label={`Dulu (${a})`} m={pair[0]} /><Caption label={`Kini (${b})`} m={pair[1]} /></div>}
      {pair?.[0] && pair?.[1] && <ShareButtons pair={pair} years={[a, b]} title={title} />}
      <p className="text-[11px] text-slate-500">Geser garis putih untuk membandingkan foto terakhir di tiap tahun. Foto adalah bukti asli dari kelompok tani.</p>
    </section>
  );
};
